if (!customElements.get('infinite-scroll')) {
  customElements.define(
    'infinite-scroll',
    class InfiniteScroll extends HTMLElement {
      constructor() {
        super();
        this.loading = false;
        this.threshold = 600; // px from bottom to trigger
      }

      connectedCallback() {
        // Hide the real pagination straight away; we drive everything ourselves
        this.hidePagination();

        // Build a loader (spinner + text) shown while fetching
        this.loader = document.createElement('div');
        this.loader.className = 'infinite-scroll__loader';
        this.loader.setAttribute('role', 'status');
        this.loader.hidden = true;
        this.loader.innerHTML =
          '<span class="infinite-scroll__spinner" aria-hidden="true"></span>' +
          '<span class="infinite-scroll__label">Loading more products…</span>';
        this.appendChild(this.loader);

        this.onScroll = this.onScroll.bind(this);
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.onScroll, { passive: true });

        // In case the first page doesn't fill the screen
        requestAnimationFrame(() => this.onScroll());
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }

      onScroll() {
        if (this.loading) return;
        if (!this.getNextUrl()) {
          this.cleanup();
          return;
        }
        const nearBottom =
          window.innerHeight + window.scrollY >=
          document.documentElement.scrollHeight - this.threshold;
        if (nearBottom) this.loadMore();
      }

      getNextUrl() {
        const nav = this.querySelector('nav.pagination, .pagination-wrapper');
        if (!nav) return null;

        let link = nav.querySelector('a[aria-label="Next page"], a.pagination__item--arrow[href]');
        if (link && link.getAttribute('href')) return link.getAttribute('href');

        const current = nav.querySelector('.pagination__item--current, [aria-current="page"]');
        const currentNum = current ? parseInt((current.textContent || '').trim(), 10) : NaN;
        if (!isNaN(currentNum)) {
          const nextLink = Array.from(nav.querySelectorAll('a.pagination__item[href]')).find(
            (a) => parseInt((a.textContent || '').trim(), 10) === currentNum + 1
          );
          if (nextLink) return nextLink.getAttribute('href');
        }
        return null;
      }

      hidePagination() {
        const nav = this.querySelector('.pagination-wrapper, nav.pagination');
        if (nav) nav.style.display = 'none';
      }

      cleanup() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
        if (this.loader) this.loader.remove();
      }

      async loadMore() {
        const url = this.getNextUrl();
        if (!url) return this.cleanup();

        this.loading = true;
        this.loader.hidden = false;

        try {
          const res = await fetch(url);
          if (!res.ok) throw new Error('HTTP ' + res.status);
          const doc = new DOMParser().parseFromString(await res.text(), 'text/html');

          const currentGrid = document.getElementById('product-grid');
          const newGrid = doc.getElementById('product-grid');
          if (currentGrid && newGrid) {
            Array.from(newGrid.querySelectorAll(':scope > li.grid__item')).forEach((li) => {
              li.classList.remove('scroll-trigger', 'animate--slide-in');
              li.removeAttribute('data-cascade');
              currentGrid.appendChild(li);
            });
          }

          // Swap in the fresh (hidden) pagination so the next "next" URL is available
          const newInf = doc.querySelector('infinite-scroll');
          const oldPag = this.querySelector('.pagination-wrapper, nav.pagination');
          const newPag = newInf ? newInf.querySelector('.pagination-wrapper, nav.pagination') : null;
          if (oldPag && newPag) {
            oldPag.replaceWith(newPag);
          } else if (oldPag) {
            oldPag.remove();
          }
          this.hidePagination();

          if (window.history && window.history.replaceState) {
            window.history.replaceState({}, '', url);
          }
        } catch (e) {
          console.error('[infinite-scroll]', e);
          const nav = this.querySelector('.pagination-wrapper, nav.pagination');
          if (nav) nav.style.display = ''; // restore clickable pagination on failure
        } finally {
          this.loading = false;
          this.loader.hidden = true;

          if (this.getNextUrl()) {
            // If content still doesn't fill the screen, keep going
            requestAnimationFrame(() => this.onScroll());
          } else {
            this.cleanup();
          }
        }
      }
    }
  );
}